import { Component, OnInit, Output, EventEmitter, ChangeDetectorRef } from '@angular/core';
import { RsbLookupModel } from 'src/app/common/model/rsb-lookup.model';

/**
 * Floor Plan Image Model
 */
export interface FloorPlanImage {
  id?: string;
  name: string;
  url: string;
  thumbnailUrl?: string;
  file?: File;
  uploadProgress?: number;
  isUploading?: boolean;
}

@Component({
  selector: 'app-projectfloorplan',
  templateUrl: './projectfloorplan.component.html',
  styleUrls: ['./projectfloorplan.component.css']
})
export class ProjectfloorplanComponent implements OnInit {
  @Output() showNextPage: EventEmitter<number> = new EventEmitter();

  // Floor Plan Data
  floorPlanImages: Array<FloorPlanImage> = [];
  selectedImageIndex: number = -1;
  
  // Upload Management
  isUploading: boolean = false;
  errorMessage: string = '';
  successMessage: string = '';
  
  // Configuration
  maxImageSize: number = 50; // MB
  maxFloorPlans: number = 999;

  constructor(
    private lookupModel: RsbLookupModel,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnInit(): void {
    this.loadExistingFloorPlans();
  }

  /**
   * Load existing floor plans from property
   */
  private loadExistingFloorPlans(): void {
    const property = this.lookupModel.getVendorProperty();
    if (property && property.floorPlanImages) {
      this.floorPlanImages = property.floorPlanImages;
    }
  }


  /**
   * Handle Floor Plan Image File Selection
   */
  onFloorPlanImageSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) {
      return;
    }

    const files = Array.from(input.files);
    this.errorMessage = '';
    this.successMessage = '';

    // Validate each file
    for (const file of files) {
      if (!this.validateFile(file)) {
        input.value = '';
        return;
      }

      // Create floor plan image object
      const floorPlanImage: FloorPlanImage = {
        id: this.generateId(),
        name: file.name,
        file: file,
        url: URL.createObjectURL(file),
        uploadProgress: 0,
        isUploading: true
      };

      this.floorPlanImages.push(floorPlanImage);
      
      // Simulate image upload
      this.simulateUpload(floorPlanImage);
    }

    input.value = '';
    this.cdr.markForCheck();
  }

  /**
   * Validate File Size and Type
   */
  private validateFile(file: File): boolean {
    // Check file type
    if (!file.type.startsWith('image/')) {
      this.errorMessage = 'Only image files are allowed';
      return false;
    }

    // Check file size
    const fileSizeMB = file.size / (1024 * 1024);
    if (fileSizeMB > this.maxImageSize) {
      this.errorMessage = `File size exceeds limit of ${this.maxImageSize}MB`;
      return false;
    }

    return true;
  }

  /**
   * Simulate Upload with Progress Tracking
   */
  private simulateUpload(floorPlan: FloorPlanImage): void {
    const interval = setInterval(() => {
      if (floorPlan.uploadProgress! < 100) {
        floorPlan.uploadProgress = floorPlan.uploadProgress! + Math.random() * 30;
        if (floorPlan.uploadProgress! > 100) {
          floorPlan.uploadProgress = 100;
        }
        this.cdr.markForCheck();
      } else {
        clearInterval(interval);
        floorPlan.isUploading = false;
        this.successMessage = `${floorPlan.name} uploaded successfully!`;
        this.cdr.markForCheck();
      }
    }, 500);
  }

  /**
   * Select Floor Plan
   */
  selectFloorPlan(index: number): void {
    this.selectedImageIndex = this.selectedImageIndex === index ? -1 : index;
  }

  /**
   * Delete Floor Plan Image
   */
  deleteFloorPlan(index: number): void {
    this.floorPlanImages.splice(index, 1);
    this.successMessage = 'Floor plan removed';
    this.selectedImageIndex = -1;
    this.cdr.markForCheck();
  }

  /**
   * Generate Unique ID
   */
  private generateId(): string {
    return 'fp_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
  }

  /**
   * Navigate Back
   */
  back(): void {
    this.showNextPage.emit(4);
  }

  /**
   * Submit Floor Plans and Continue
   */
  submit(): void {
    // Save to lookup model
    let property = this.lookupModel.getVendorProperty();
    property.floorPlanImages = this.floorPlanImages;
    this.lookupModel.setVendorProperty(property);

    this.successMessage = 'Floor plans saved successfully!';
    setTimeout(() => {
      this.showNextPage.emit(6);
    }, 500);
  }
}

